import React from 'react';
import theme from '../../theme';
import styles from './Button.module.css';

export default function Button({
  children,
  variant = 'contained',
  size = 'medium',
  color = 'primary',
  role = 'admin',
  className = '',
  style = {},
  disabled = false,
  fullWidth = false,
  startIcon,
  endIcon,
  ...props
}) {
  const palette = theme.rolePalettes[role] || theme.rolePalettes.admin;
  const colorObj = palette[color] || palette.primary;
  const tokens = theme.componentTokens.button;

  const getVariantStyles = () => {
    switch (variant) {
      case 'contained':
        return {
          background: colorObj.main,
          color: colorObj.contrastText,
          border: `1px solid ${colorObj.main}`,
          boxShadow: tokens.boxShadow,
          '&:hover': {
            background: colorObj.dark || colorObj.main,
            boxShadow: tokens.hoverShadow,
            transform: 'translateY(-1px)',
          },
          '&:active': {
            background: colorObj.dark || colorObj.main,
            transform: 'translateY(0) scale(0.98)',
          }
        };
      case 'outlined':
        return {
          background: 'transparent',
          color: colorObj.main,
          border: `1px solid ${colorObj.main}`,
          boxShadow: 'none',
          '&:hover': {
            background: `${colorObj.main}10`,
            borderColor: colorObj.dark || colorObj.main,
            boxShadow: tokens.hoverShadow,
          }
        };
      case 'text':
        return {
          background: 'transparent',
          color: colorObj.main,
          border: '1px solid transparent',
          boxShadow: 'none',
          '&:hover': {
            background: `${colorObj.main}10`,
          }
        };
      default:
        return {};
    }
  };

  const getSizeStyles = () => {
    switch (size) {
      case 'small':
        return {
          padding: `${theme.spacing}px ${theme.spacing * 2}px`,
          fontSize: '0.75rem',
        };
      case 'medium':
        return {
          padding: tokens.padding,
          fontSize: tokens.fontSize,
        };
      case 'large':
        return {
          padding: `${theme.spacing * 2}px ${theme.spacing * 4}px`,
          fontSize: '1rem',
        };
      default:
        return {};
    }
  };

  const buttonStyles = {
    borderRadius: tokens.borderRadius,
    fontWeight: tokens.fontWeight,
    textTransform: tokens.textTransform,
    border: 'none',
    cursor: disabled ? 'not-allowed' : 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacingScale[2], // 8px gap
    width: fullWidth ? '100%' : 'auto',
    transition: `all ${theme.motion.duration.normal} ${theme.motion.easing.easeInOut}`,
    fontFamily: theme.typography.fontFamily,
    letterSpacing: '0.01em',
    ...getVariantStyles(),
    ...getSizeStyles(),
    ...style,
    ...(disabled && {
      opacity: 0.5,
      cursor: 'not-allowed',
      pointerEvents: 'none',
    })
  };

  return (
    <button
      className={`${styles.button} ${className}`}
      style={buttonStyles}
      disabled={disabled}
      {...props}
    >
      {startIcon && <span className={styles.startIcon}>{startIcon}</span>}
      {children}
      {endIcon && <span className={styles.endIcon}>{endIcon}</span>}
    </button>
  );
} 